import React, { useMemo, useState } from 'react';
import Badge from './ui/Badge';
import Button from './ui/Button';
import Icons from './Icons';
import Modal from './ui/Modal';
import { formatDate } from '../utils/dateFormat';

const defaultCategories = ['UC', 'Medical', 'Social', 'Police', 'Other'];

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return null;
  return date;
};

const resolveCategory = (doc) => {
  const tags = doc.tags || [];
  const matched = tags.find((tag) => defaultCategories.includes(tag));
  if (matched) return matched;
  return tags[0] || 'Other';
};

const guessMimeType = (fileName = '') => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (!ext) return '';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return `image/${ext === 'jpg' ? 'jpeg' : ext}`;
  if (['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(ext)) return `audio/${ext === 'm4a' ? 'mp4' : ext}`;
  if (['mp4', 'webm', 'ogg', 'mov'].includes(ext)) return `video/${ext === 'mov' ? 'quicktime' : ext}`;
  if (ext === 'pdf') return 'application/pdf';
  return '';
};

const resolveFileInfo = (doc) => {
  const metadata = doc.metadata || {};
  const fileName = metadata.fileName || doc.fileName || doc.name || '';
  const url = metadata.fileUrl || metadata.url || doc.fileUrl || doc.url || '';
  const type = metadata.type || guessMimeType(fileName);
  const isImage = type.startsWith('image/');
  const isAudio = type.startsWith('audio/');
  const isVideo = type.startsWith('video/');
  const isPdf = type === 'application/pdf';
  return {
    name: fileName,
    url,
    type,
    isImage,
    isAudio,
    isVideo,
    isPdf
  };
};

const downloadFile = (fileInfo) => {
  if (!fileInfo?.url) return;
  const link = document.createElement('a');
  link.href = fileInfo.url;
  link.download = fileInfo.name || 'document';
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export default function DocumentsPanel({
  documents = [],
  onUpload,
  onRename,
  onUpdate,
  onArchive,
  onRestore,
  onDelete,
  canDelete = false,
  canDownload = true,
  canView = true
}) {
  const now = useMemo(() => new Date(), []);
  const [showArchived, setShowArchived] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [editingNotesId, setEditingNotesId] = useState(null);
  const [draftNotes, setDraftNotes] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);

  const getDueStatus = (value) => {
    const date = parseDate(value);
    if (!date) return null;
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { label: 'Overdue', variant: 'danger' };
    if (diffDays <= 14) return { label: 'Due soon', variant: 'review' };
    return null;
  };

  const getExpiryStatus = (value) => {
    const date = parseDate(value);
    if (!date) return null;
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { label: 'Expired', variant: 'danger' };
    if (diffDays <= 30) return { label: 'Expiring soon', variant: 'review' };
    return null;
  };

  const grouped = useMemo(() => {
    const map = new Map(defaultCategories.map((category) => [category, []]));
    documents.forEach((doc) => {
      const category = resolveCategory(doc);
      if (!map.has(category)) map.set(category, []);
      map.get(category).push(doc);
    });
    const customCategories = [...map.keys()].filter((category) => !defaultCategories.includes(category));
    const ordered = [...defaultCategories, ...customCategories.sort((a, b) => a.localeCompare(b))];
    return ordered.map((category) => ({
      category,
      items: map.get(category) || []
    }));
  }, [documents]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Documents & Evidence</h3>
        <div className="flex items-center gap-2">
          {documents.some((doc) => doc.archived) && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowArchived((prev) => !prev)}
              type="button"
            >
              {showArchived ? 'Hide archived' : 'Show archived'}
            </Button>
          )}
          <Button size="sm" variant="secondary" onClick={() => onUpload?.()} type="button">
            <Icons.Upload /> Upload
          </Button>
        </div>
      </div>

      {documents.length === 0 && (
        <div className="text-sm sp-muted">No documents uploaded yet.</div>
      )}

      {grouped.map(({ category, items }) => (
        <div key={category} className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h4 className="sp-value" style={{ fontWeight: 600 }}>{category}</h4>
            <span className="text-xs sp-muted">{items.length} document{items.length === 1 ? '' : 's'}</span>
          </div>
          {items.filter((doc) => (showArchived ? true : !doc.archived)).length === 0 ? (
            <div className="text-sm sp-muted">No documents in this section.</div>
          ) : (
            <div className="space-y-3">
              {items
                .filter((doc) => (showArchived ? true : !doc.archived))
                .map((doc) => {
                  const dueDate = doc.dueDate || doc.metadata?.dueDate;
                  const expiryDate = doc.expiryDate || doc.metadata?.expiryDate;
                  const dueStatus = getDueStatus(dueDate);
                  const expiryStatus = getExpiryStatus(expiryDate);
                  const isEditing = editingId === doc.id;
                  const isEditingNotes = editingNotesId === doc.id;
                  const fileInfo = resolveFileInfo(doc);
                  return (
                <div key={doc.id} className="sp-panel p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <input
                            className="sp-input"
                            value={draftName}
                            onChange={(event) => setDraftName(event.target.value)}
                            placeholder="Document name"
                          />
                          <Button
                            size="sm"
                            variant="secondary"
                            type="button"
                            onClick={() => {
                              const nextName = draftName.trim();
                              if (!nextName) return;
                              onRename?.(doc.id, nextName);
                              setEditingId(null);
                              setDraftName('');
                            }}
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            type="button"
                            onClick={() => {
                              setEditingId(null);
                              setDraftName('');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="sp-value" style={{ fontWeight: 600 }}>{doc.name}</h4>
                          {doc.archived && <Badge variant="inactive">Archived</Badge>}
                        </div>
                      )}
                      <p className="text-xs sp-muted">
                        {doc.metadata?.type || 'File'} - {doc.metadata?.size || 'N/A'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {(doc.tags || [])
                        .filter((tag) => tag !== category)
                        .map((tag) => (
                          <Badge key={tag} variant="info" className="sp-badge--subtle">
                            {tag}
                          </Badge>
                        ))}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs sp-muted">
                    {dueDate && (
                      <span className="flex items-center gap-2">
                        Due by {formatDate(dueDate)}
                        {dueStatus && <Badge variant={dueStatus.variant}>{dueStatus.label}</Badge>}
                      </span>
                    )}
                    {expiryDate && (
                      <span className="flex items-center gap-2">
                        Expires {formatDate(expiryDate)}
                        {expiryStatus && <Badge variant={expiryStatus.variant}>{expiryStatus.label}</Badge>}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 text-sm">
                    <div className="sp-label">Notes</div>
                    {isEditingNotes ? (
                      <div className="mt-2 space-y-2">
                        <textarea
                          className="sp-textarea w-full"
                          rows={3}
                          value={draftNotes}
                          onChange={(event) => setDraftNotes(event.target.value)}
                          placeholder="Add notes for this document"
                        />
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            type="button"
                            onClick={() => {
                              onUpdate?.(doc.id, { notes: draftNotes.trim() });
                              setEditingNotesId(null);
                              setDraftNotes('');
                            }}
                          >
                            Save notes
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            type="button"
                            onClick={() => {
                              setEditingNotesId(null);
                              setDraftNotes('');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 text-sm sp-muted">
                        {doc.notes ? doc.notes : 'No notes added.'}
                      </div>
                    )}
                  </div>
                  <div className="mt-3 text-xs sp-muted">
                    Versions: {doc.versions?.length || 0} | Visibility: {doc.visibility?.join(', ') || 'All'}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {fileInfo.url && canView && (
                      <Button
                        size="sm"
                        variant="secondary"
                        type="button"
                        onClick={() => setPreviewDoc({ doc, fileInfo })}
                      >
                        View file
                      </Button>
                    )}
                    {fileInfo.url && canDownload && (
                      <Button
                        size="sm"
                        variant="ghost"
                        type="button"
                        onClick={() => downloadFile(fileInfo)}
                      >
                        Download
                      </Button>
                    )}
                    {!isEditing && (
                      <Button
                        size="sm"
                        variant="ghost"
                        type="button"
                        onClick={() => {
                          setEditingId(doc.id);
                          setDraftName(doc.name);
                        }}
                      >
                        Rename
                      </Button>
                    )}
                    {!doc.archived ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        type="button"
                        onClick={() => onArchive?.(doc.id)}
                      >
                        Archive
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        type="button"
                        onClick={() => onRestore?.(doc.id)}
                      >
                        Restore
                      </Button>
                    )}
                    {!isEditingNotes && (
                      <Button
                        size="sm"
                        variant="ghost"
                        type="button"
                        onClick={() => {
                          setEditingNotesId(doc.id);
                          setDraftNotes(doc.notes || '');
                        }}
                      >
                        {doc.notes ? 'Edit notes' : 'Add notes'}
                      </Button>
                    )}
                    {canDelete && onDelete && (
                      <Button
                        size="sm"
                        variant="ghost"
                        type="button"
                        onClick={() => {
                          if (window.confirm('Delete this document? This cannot be undone.')) {
                            onDelete(doc.id);
                          }
                        }}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              )})}
            </div>
          )}
        </div>
      ))}

      <Modal
        isOpen={Boolean(previewDoc)}
        onClose={() => setPreviewDoc(null)}
      >
        {previewDoc && (
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <h3 className="sp-value" style={{ fontWeight: 600 }}>
                  {previewDoc.doc.name || previewDoc.fileInfo.name || 'Document'}
                </h3>
                <p className="text-xs sp-muted">{previewDoc.fileInfo.type || 'File preview'}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setPreviewDoc(null)}>
                Close
              </Button>
            </div>
            <div className="sp-panel sp-panel--muted p-4">
              {previewDoc.fileInfo.isImage && (
                <img
                  src={previewDoc.fileInfo.url}
                  alt={previewDoc.doc.name || 'Document preview'}
                  className="max-h-[70vh] mx-auto"
                />
              )}
              {previewDoc.fileInfo.isPdf && (
                <iframe
                  title={previewDoc.doc.name || 'PDF preview'}
                  src={previewDoc.fileInfo.url}
                  className="w-full h-[70vh]"
                />
              )}
              {previewDoc.fileInfo.isAudio && (
                <audio controls src={previewDoc.fileInfo.url} className="w-full">
                  Your browser does not support audio playback.
                </audio>
              )}
              {previewDoc.fileInfo.isVideo && (
                <video controls src={previewDoc.fileInfo.url} className="w-full max-h-[70vh]">
                  Your browser does not support video playback.
                </video>
              )}
              {!previewDoc.fileInfo.isImage &&
                !previewDoc.fileInfo.isPdf &&
                !previewDoc.fileInfo.isAudio &&
                !previewDoc.fileInfo.isVideo && (
                  <div className="text-sm sp-muted">
                    Preview is not available for this file type.
                  </div>
              )}
            </div>
            {previewDoc.fileInfo.url && (
              <div className="flex items-center gap-3">
                <a
                  className="sp-link text-sm"
                  href={previewDoc.fileInfo.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open in new tab
                </a>
                <Button size="sm" variant="ghost" type="button" onClick={() => downloadFile(previewDoc.fileInfo)}>
                  Download
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
